import React from 'react';
import { ThreadMessage, TrialRequestPayload } from '../../types/thread';
import { formatDateTime } from '../../utils/contractHelpers'; // Reusing date formatter
import { FileText, DollarSign, Calendar, ArrowRight, CheckCircle, ClipboardPen, Check, X } from 'lucide-react';

interface MessageBubbleProps {
    message: ThreadMessage;
    isOwn: boolean;
    senderName?: string;
    senderAvatar?: string;
    senderId?: string;
    onAvatarClick?: (userId: string) => void;
    onAction?: (action: string, payload?: any) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
    message,
    isOwn,
    senderName,
    senderAvatar,
    senderId,
    onAvatarClick,
    onAction
}) => {
    const formatAmount = (amount?: number) => {
        if (!amount || amount <= 0) {
            return '免费';
        }

        return new Intl.NumberFormat('zh-CN', {
            style: 'currency',
            currency: 'CNY',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const renderContent = () => {
        switch (message.type) {
            case 'PROPOSAL':
                const proposal = message.payload as any; // Cast for now
                return (
                    <div className="relative bg-white border border-gray-200 rounded-xl p-4 min-w-[280px]">
                        {/* Clickable overlay for viewing (read-only) */}
                        <div
                            onClick={() => onAction?.('VIEW_PROPOSAL', { ...proposal, readOnly: true })}
                            className="cursor-pointer"
                        >
                            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                                <FileText className="w-4 h-4 text-blue-600" />
                                <span className="font-bold text-gray-900">合作方案</span>
                                {isOwn && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAction?.('EDIT_PROPOSAL', proposal);
                                        }}
                                        className="ml-auto px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                                    >
                                        编辑
                                    </button>
                                )}
                            </div>
                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{proposal.description}</p>

                            <div className="space-y-2 mb-4">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> 预算</span>
                                    <span className="font-bold text-gray-900">{proposal.currency} {(proposal.budget ?? 0).toLocaleString()}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> 交付</span>
                                    <span className="font-bold text-gray-900">{formatDateTime(proposal.deliveryDate).split(' ')[0]}</span>
                                </div>
                            </div>
                        </div>
                        {!isOwn && (
                            <button
                                onClick={() => onAction?.('INVITE_COOPERATION_FROM_PROPOSAL', proposal)}
                                className="w-full mt-3 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                            >
                                请求合同
                            </button>
                        )}
                    </div>
                );
            case 'INVITATION':
                const invite = message.payload as any;
                const isProposalRef = invite.title === '合作方案参考';
                const hasDetails = isProposalRef || (invite.title && (invite.description || invite.budgetRange || invite.priceRange || invite.tags?.length));
                const handleInviteClick = () => {
                    if (isProposalRef) {
                        onAction?.('VIEW_PROPOSAL', { description: invite.description, budget: parseFloat((invite.budgetRange || '0').replace(/[^0-9.]/g, '')), currency: 'CNY', deliveryDate: Date.now(), milestones: [], status: 'PENDING', readOnly: true });
                    } else if (invite.briefId) onAction?.('VIEW_BRIEF', invite.briefId);
                    else if (invite.serviceId) onAction?.('VIEW_SERVICE', invite.serviceId);
                };
                return (
                    <div
                        className={`bg-white border border-gray-200 rounded-xl p-4 min-w-[280px] ${(invite.briefId || invite.serviceId || isProposalRef) ? 'cursor-pointer hover:border-gray-300 transition-colors' : ''}`}
                        onClick={(invite.briefId || invite.serviceId || isProposalRef) ? handleInviteClick : undefined}
                    >
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                            <CheckCircle className="w-4 h-4 text-purple-600" />
                            <span className="font-bold text-gray-900">{invite.briefId ? '需求参考' : invite.serviceId ? '服务参考' : '合作邀请'}</span>
                        </div>
                        {hasDetails ? (
                            <>
                                <p className="text-sm font-semibold text-gray-900 mb-2">{invite.title}</p>
                                {invite.description && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{invite.description}</p>}
                                <div className="space-y-1 mb-3">
                                    {invite.budgetRange && (
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-gray-400">预算</span>
                                            <span className="font-medium text-gray-700">{invite.budgetRange}</span>
                                        </div>
                                    )}
                                    {invite.priceRange && (
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-gray-400">价格</span>
                                            <span className="font-medium text-gray-700">{invite.priceRange}</span>
                                        </div>
                                    )}
                                    {invite.deliveryTimeRange && (
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-gray-400">交付</span>
                                            <span className="font-medium text-gray-700">{invite.deliveryTimeRange}</span>
                                        </div>
                                    )}
                                </div>
                                {invite.tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-3">
                                        {invite.tags.slice(0, 4).map((t: string, i: number) => (
                                            <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{t}</span>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-gray-600 mb-3">{invite.text}</p>
                        )}
                        {!isOwn && invite.briefId && invite.isProposalRequest && (
                            <button
                                onClick={e => { e.stopPropagation(); onAction?.('OPEN_PROPOSAL_DIALOG'); }}
                                className="w-full mt-1 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                            >
                                提交方案
                            </button>
                        )}
                        {!isOwn && invite.briefId && !invite.isProposalRequest && (
                            <div className="flex gap-2 mt-1">
                                <button
                                    onClick={e => { e.stopPropagation(); onAction?.('REQUEST_PROPOSAL', invite); }}
                                    className="flex-1 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                                >
                                    请求方案
                                </button>
                                <button
                                    onClick={e => { e.stopPropagation(); onAction?.('REQUEST_TRIAL', invite); }}
                                    className="flex-1 py-2 border border-black text-black rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                                >
                                    发起试稿
                                </button>
                            </div>
                        )}
                        {!isOwn && isProposalRef && (
                            <button
                                onClick={e => { e.stopPropagation(); onAction?.('START_COOPERATION'); }}
                                className="w-full mt-1 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                            >
                                发起合同
                            </button>
                        )}
                    </div>
                );
            case 'TRIAL_REQUEST':
                const trialRequest = message.payload as TrialRequestPayload;
                const trialStatusClassName = trialRequest.status === 'ACCEPTED'
                    ? 'bg-green-50 text-green-700'
                    : trialRequest.status === 'REJECTED'
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-gray-100 text-gray-700';
                return (
                    <div className="relative bg-white border border-gray-200 rounded-xl p-4 min-w-[280px]">
                        <div
                            className="cursor-pointer"
                            onClick={() => onAction?.('VIEW_BRIEF', trialRequest.briefId)}
                        >
                            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                                <ClipboardPen className="w-4 h-4 text-blue-600" />
                                <span className="font-bold text-gray-900">试稿请求</span>
                                <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${trialStatusClassName}`}>
                                    {trialRequest.status === 'ACCEPTED' ? '已接受' : trialRequest.status === 'REJECTED' ? '已拒绝' : '待处理'}
                                </span>
                            </div>

                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{trialRequest.briefTitle}</p>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 flex items-center gap-1">
                                        <FileText className="w-3 h-3" />
                                        需求
                                    </span>
                                    <span className="font-bold text-gray-900 max-w-[150px] truncate">{trialRequest.briefTitle}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 flex items-center gap-1">
                                        <DollarSign className="w-3 h-3" />
                                        预算
                                    </span>
                                    <span className="font-semibold text-gray-900">{trialRequest.budgetRange || '可谈'}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 flex items-center gap-1">
                                        <DollarSign className="w-3 h-3" />
                                        试稿费
                                    </span>
                                    <span className="font-semibold text-gray-900">{formatAmount(trialRequest.trialFeeAmount)}</span>
                                </div>
                            </div>
                        </div>

                        {!isOwn && trialRequest.status === 'PENDING' && (
                            <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                                <button
                                    onClick={() => onAction?.('RESPOND_TRIAL_REQUEST', {
                                        messageId: message.id,
                                        status: 'REJECTED',
                                        briefId: trialRequest.briefId,
                                        trialRequestPayload: trialRequest
                                    })}
                                    className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                                >
                                    <X className="w-4 h-4" />
                                    拒绝
                                </button>
                                <button
                                    onClick={() => onAction?.('RESPOND_TRIAL_REQUEST', {
                                        messageId: message.id,
                                        status: 'ACCEPTED',
                                        briefId: trialRequest.briefId,
                                        trialRequestPayload: trialRequest
                                    })}
                                    className="flex-1 py-2 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Check className="w-4 h-4" />
                                    接受试稿
                                </button>
                            </div>
                        )}

                        {trialRequest.status === 'ACCEPTED' && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <button
                                    onClick={() => onAction?.('VIEW_TRIAL', trialRequest.trialId)}
                                    className="w-full py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                                >
                                    查看试稿 <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </div>
                );
            case 'SYSTEM':
                const sysPayload = message.payload as any;
                if (sysPayload?.contractId) {
                    return (
                        <div className="bg-white border border-gray-200 rounded-xl p-4 min-w-[260px]">
                            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                                <FileText className="w-4 h-4 text-green-600" />
                                <span className="font-bold text-gray-900">{sysPayload.title || '合作合同'}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{message.content}</p>
                            <button
                                onClick={() => onAction?.('VIEW_CONTRACT', sysPayload.contractId)}
                                className="w-full py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                            >
                                查看合同 <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    );
                }
                return (
                    <div className="inline-flex items-center rounded-full bg-gray-100 px-4 py-2 text-sm text-gray-600">
                        {message.content}
                    </div>
                );
            default:
                return <p className="text-sm whitespace-pre-wrap">{message.content}</p>;
        }
    };

    const canOpenSenderProfile = Boolean(!isOwn && senderId && senderId !== 'system' && onAvatarClick);
    const senderAvatarNode = senderAvatar ? (
        <img src={senderAvatar} alt={senderName} className="w-8 h-8 rounded-full object-cover" />
    ) : (
        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500">
            {(senderName || '?')[0]}
        </div>
    );

    return (
        <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}>
            {!isOwn && (
                <div className="flex-shrink-0 mr-2">
                    {canOpenSenderProfile ? (
                        <button
                            type="button"
                            aria-label={`查看${senderName || '对方'}主页`}
                            title="查看主页"
                            className="block rounded-full transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            onClick={(event) => {
                                event.stopPropagation();
                                if (senderId) onAvatarClick?.(senderId);
                            }}
                        >
                            {senderAvatarNode}
                        </button>
                    ) : (
                        senderAvatarNode
                    )}
                </div>
            )}
            <div className="max-w-[70%]">
                {!isOwn && senderName && (
                    <p className="text-xs text-gray-400 mb-1 ml-1">{senderName}</p>
                )}
                <div className={`${message.type === 'TEXT' ? (isOwn ? 'bg-blue-600 text-white rounded-l-2xl rounded-tr-2xl px-4 py-3' : 'bg-gray-100 text-gray-900 rounded-r-2xl rounded-tl-2xl px-4 py-3') : ''}`}>
                    {renderContent()}
                    <div className={`text-[10px] mt-1 ${isOwn ? 'text-right text-blue-200' : 'text-left text-gray-400'}`}>
                        {formatDateTime(message.createdAt)}
                    </div>
                </div>
            </div>
            {isOwn && (
                <div className="flex-shrink-0 ml-2">
                    {senderAvatar ? (
                        <img src={senderAvatar} alt={senderName} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs text-blue-600">
                            {(senderName || 'Me')[0]}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
